import {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import type { User } from "@/types";
import { authService, authApi } from "../services/auth";

interface AuthContextType {
  user: User | null;
  otpLogin: (email: string) => Promise<{
    success: boolean;
    testOtp?: string;
    error?: string;
    isNewUser?: boolean;
  }>;
  verifyOtpLogin: (
    email: string,
    otp: string
  ) => Promise<{ success: boolean; user?: User; error?: string }>;
  otpSignup: (email: string) => Promise<{
    success: boolean;
    testOtp?: string;
    error?: string;
    isNewUser?: boolean;
  }>;
  verifyOtpSignup: (
    email: string,
    otp: string
  ) => Promise<{ success: boolean; user?: User; error?: string }>;
  startOtpFlow: (email: string) => Promise<{
    success: boolean;
    testOtp?: string;
    error?: string;
    isNewUser?: boolean;
  }>;
  verifyOtp: (
    email: string,
    otp: string
  ) => Promise<{ success: boolean; user?: User; error?: string }>;
  googleSignIn: () => Promise<{
    success: boolean;
    user?: User;
    error?: string;
  }>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [onboardingStatus, setOnboardingStatus] = useState<null | {
    isOnboardingComplete: boolean;
    completedSteps: string[];
    currentStep: string;
    servicePlans: any[];
  }>(null);

  // Check for existing session on mount
  useEffect(() => {
    checkExistingSession();
  }, []);

  const checkExistingSession = async () => {
    console.log("🔍 Checking existing session on app startup...");

    try {
      const token = localStorage.getItem("shopglobal_auth_token");
      const storedUser = localStorage.getItem("shopglobal_user");

      console.log("📦 Token in localStorage:", token ? "Found" : "Not found");
      console.log(
        "👤 User in localStorage:",
        storedUser ? "Found" : "Not found"
      );

      if (!token) {
        console.log("⚠️ No auth token found, setting loading to false");
        // Clear any leftover user data
        if (storedUser) {
          localStorage.removeItem("shopglobal_user");
        }
        setIsLoading(false);
        return;
      }

      console.log("🔐 Token exists, verifying with server...");
      // Verify session with server
      try {
        const sessionResponse = await authApi.verifySession();
        console.log("✅ Session verification successful:", sessionResponse);

        // If verification passes, load user from storage
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          console.log("👥 Setting user data:", userData);
          setUser(userData);
        } else {
          console.log("⚠️ No stored user data found, but session was valid");
        }
      } catch (error: any) {
        console.error("❌ Session verification failed:", error);

        // Only log the error, don't clear the session if it's a network error
        if (
          error?.message?.includes("Invalid or expired session") ||
          error?.message?.includes("No token provided")
        ) {
          console.log("🧹 Clearing invalid session data");
          // Clear invalid session
          authApi.logout();
          setUser(null);
        } else {
          console.log("⚠️ Network or server error, keeping session data");
        }
      }
    } catch (error) {
      console.error("❌ Session check failed:", error);
      console.error("🧹 Clearing session data due to error");

      // Clear any invalid session data
      authApi.logout();
      setUser(null);
    } finally {
      console.log("🏁 Session check complete, setting loading to false");
      setIsLoading(false);
    }
  };

  // Unified OTP flow - works for both sign in and sign up
  const startOtpFlow = async (email: string) => {
    try {
      // Use the unified OTP start endpoint
      const response = await fetch(
        `${import.meta.env.DEV ? "http://localhost:8787" : "https://shopglobal.finhub.workers.dev"}/api/auth/otp/start`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Failed to send OTP" }));
        return {
          success: false,
          error: errorData.error || "Failed to send verification code",
        };
      }

      const result = await response.json();
      return {
        success: true,
        testOtp: result.testOtp,
        message: result.message || "Verification code sent",
        isNewUser: result.isNewUser,
      };
    } catch (error) {
      console.error("OTP flow start error:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to send verification code",
      };
    }
  };

  const verifyOtp = async (email: string, otp: string) => {
    try {
      console.log("🔐 Verifying OTP:", email, otp);

      // Use the unified signup/complete endpoint
      const response = await fetch(
        `${import.meta.env.DEV ? "http://localhost:8787" : "https://shopglobal.finhub.workers.dev"}/api/auth/signup/complete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, otp }),
        }
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Verification failed" }));
        console.error("❌ OTP verification failed:", errorData);
        return {
          success: false,
          error: errorData.error || "Verification failed",
        };
      }

      const result = await response.json();
      console.log("✅ Authentication successful:", result);

      if (result.success && result.user && result.session?.token) {
        const user: User = {
          id: result.user.id,
          email: result.user.email,
          fullName: result.user.full_name,
          membershipTier:
            (result.user.membershipTier?.toLowerCase() as
              | "free"
              | "premium"
              | "business") || "free",
          delawareAddress: result.user.delawareAddress,
          canadianAddress: result.user.canadianAddress
            ? typeof result.user.canadianAddress === "string"
              ? JSON.parse(result.user.canadianAddress)
              : result.user.canadianAddress
            : {
                street: "",
                city: "",
                province: "",
                postalCode: "",
              },
          verified: true,
          role: "user" as const,
          createdAt: new Date().toISOString(),
        };

        console.log("💾 Storing auth data to localStorage...");
        // Store auth data - auth service handles token management internally
        localStorage.setItem("shopglobal_user", JSON.stringify(user));

        // Set user in state
        setUser(user);
        console.log("✅ User set in auth state");

        return {
          success: true,
          user: user,
        };
      } else {
        console.error("❌ Invalid authentication response:", result);
        return { success: false, error: "Authentication failed" };
      }
    } catch (error) {
      console.error("❌ OTP verification error:", error);
      return {
        success: false,
        error: "Verification failed. Please try again.",
      };
    }
  };

  const googleSignIn = async () => {
    try {
      console.log("🔐 Starting Google Sign In...");

      // Open Google OAuth popup
      const baseUrl = import.meta.env.DEV
        ? "http://localhost:8787"
        : "https://shopglobal.finhub.workers.dev";

      const googleAuthUrl = `${baseUrl}/api/auth/google/authorize`;

      // Open popup window
      const popup = window.open(
        googleAuthUrl,
        "google-auth",
        "width=500,height=600,scrollbars=yes,resizable=yes"
      );

      if (!popup) {
        return {
          success: false,
          error: "Popup blocked. Please allow popups for this site.",
        };
      }

      // Listen for popup messages
      return new Promise<{ success: boolean; user?: User; error?: string }>(
        (resolve) => {
          const messageHandler = (event: MessageEvent) => {
            // Verify origin for security
            if (event.origin !== baseUrl) {
              return;
            }

            if (event.data.type === "GOOGLE_AUTH_SUCCESS") {
              popup.close();
              window.removeEventListener("message", messageHandler);

              const { user: userData, session } = event.data;

              if (userData && session?.token) {
                const user: User = {
                  id: userData.id,
                  email: userData.email,
                  fullName: userData.full_name,
                  membershipTier:
                    (userData.membershipTier?.toLowerCase() as
                      | "free"
                      | "premium"
                      | "business") || "free",
                  delawareAddress: userData.delawareAddress,
                  canadianAddress: userData.canadianAddress
                    ? typeof userData.canadianAddress === "string"
                      ? JSON.parse(userData.canadianAddress)
                      : userData.canadianAddress
                    : {
                        street: "",
                        city: "",
                        province: "",
                        postalCode: "",
                      },
                  verified: userData.verified || true,
                  role: "user" as const,
                  createdAt: userData.createdAt || new Date().toISOString(),
                };

                // Store auth data
                localStorage.setItem("shopglobal_user", JSON.stringify(user));
                localStorage.setItem("shopglobal_auth_token", session.token);

                // Set user in state
                setUser(user);

                resolve({
                  success: true,
                  user: user,
                });
              } else {
                resolve({
                  success: false,
                  error: "Authentication failed - no user data received",
                });
              }
            } else if (event.data.type === "GOOGLE_AUTH_ERROR") {
              popup.close();
              window.removeEventListener("message", messageHandler);

              resolve({
                success: false,
                error: event.data.error || "Google authentication failed",
              });
            }
          };

          window.addEventListener("message", messageHandler);

          // Timeout after 5 minutes
          setTimeout(() => {
            popup.close();
            window.removeEventListener("message", messageHandler);
            resolve({
              success: false,
              error: "Authentication timeout. Please try again.",
            });
          }, 300000);
        }
      );
    } catch (error) {
      console.error("❌ Google Sign In error:", error);
      return {
        success: false,
        error: "Google Sign In failed. Please try again.",
      };
    }
  };

  const logout = () => {
    setUser(null);
    authApi.logout();
  };

  const value: AuthContextType = {
    user,
    otpLogin: startOtpFlow,
    verifyOtpLogin: verifyOtp,
    otpSignup: startOtpFlow,
    verifyOtpSignup: verifyOtp,
    startOtpFlow,
    verifyOtp,
    googleSignIn,
    logout,
    isLoading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
